import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { DOC_UPLOAD_PAGE_STATE } from "../../utility/enums/common";
import { RootState } from "../store";
import {
  DocStatusCards,
  DownloadNecessaryDocUploadItem,
  DocUploadSelectOptions,
  ActionPath,
} from "../../utility/types/common";
import { RequiredPagesType } from "../../components/DocUpload/types";
import { AttachmentType } from "../../routes/tasks/document/models";

export interface UploadFileMeta {
  sha256: string;
  name: string;
  type: string;
  side: string;
  size: string;
  id: string;
  documentTypeId: string;
}

export enum CandidateBgcAttachmentSubType {
  FRONT = "Front",
  BACK = "Back",
}

export enum FileChangeEvent {
  ADDED = "added",
  REMOVED = "removed",
}

export type OldUiFlyoutContentType = DocUploadState["oldUiFlyoutContent"];
interface DocUploadState {
  currentPage: DOC_UPLOAD_PAGE_STATE;
  docStatusCards: DocStatusCards[];
  isActionButtonEnabled: boolean;
  selectedFileType: DocUploadSelectOptions | null;
  requiredPages: Array<RequiredPagesType>;
  isCountryWithDownloadableContentDocUpload: boolean;
  downloadableDocs: {
    docs: DownloadNecessaryDocUploadItem[];
    showErrorBanner: boolean;
  };
  files: Record<string, UploadFileMeta | undefined>;
  oldUiFlyoutContent: {
    documentType: AttachmentType | "FORM";
    isEdit: boolean;
    buttonDelegationEnabled: boolean | null;
  };
}

export const initialState: DocUploadState = {
  currentPage: DOC_UPLOAD_PAGE_STATE.SELECT_DOCUMENT_TYPE,
  docStatusCards: [],
  isActionButtonEnabled: false,
  isCountryWithDownloadableContentDocUpload: false,
  downloadableDocs: { docs: [], showErrorBanner: false },
  selectedFileType: null,
  requiredPages: ["Front", "Back"],
  files: {},
  oldUiFlyoutContent: {
    // documentType: AttachmentType.MEXICAN_PASSPORT,
    documentType: "FORM",
    isEdit: false,
    buttonDelegationEnabled: true,
  },
};

const docUploadSlice = createSlice({
  name: "docUpload",
  initialState,
  reducers: {
    setActivePage: (state, action: PayloadAction<{ page: DocUploadState["currentPage"] }>) => {
      state.currentPage = action.payload.page;
    },
    toggleSubmitButton: (state, action: PayloadAction<{ isEnabled: boolean }>) => {
      const { isEnabled } = action.payload;
      state.isActionButtonEnabled = isEnabled;
    },
    setFile: (state, action: PayloadAction<UploadFileMeta>) => {
      state.files[action.payload.side] = action.payload;
    },
    removeFile: (state, action: PayloadAction<UploadFileMeta>) => {
      state.files[action.payload.side] = undefined;
    },
    removeAllFiles: (state) => {
      state.files = {};
    },
    setIsCountryWithDownloadableContent: (state, action: PayloadAction<boolean>) => {
      state.isCountryWithDownloadableContentDocUpload = action.payload;
    },
    setDownloadableDocs: (
      state,
      action: PayloadAction<{ downloadableDocs: DownloadNecessaryDocUploadItem[]; showErrorBanner: boolean }>
    ) => {
      const { downloadableDocs, showErrorBanner } = action.payload;
      state.downloadableDocs = {
        showErrorBanner,
        docs: downloadableDocs,
      };
    },
    removeFromDocStatusCards: (state, action: PayloadAction<{ documentId: string }>) => {
      const { documentId } = action.payload;
      const docs = state.docStatusCards.map((doc) => {
        if (doc.documentId === documentId) return { ...doc, markForDeletion: true };

        return doc;
      });

      state.docStatusCards = docs;
    },
    setRequiredPagesForFileUpload: (state, action: PayloadAction<{ pages: Array<RequiredPagesType> }>) => {
      const { pages } = action.payload;

      state.requiredPages = pages;
    },
    setSelectedFileType: (state, action: PayloadAction<{ fileType: DocUploadSelectOptions }>) => {
      const { fileType } = action.payload;

      state.selectedFileType = fileType;
    },
    updateDocStatusCardsFromEDM: (state, action: PayloadAction<{ documents: DocStatusCards[] }>) => {
      const { documents } = action.payload;

      // Not care about any incoming data if docs already markForDeletion
      const retainedDocs: DocStatusCards[] = [];
      const retainedDocIds: string[] = [];

      const markForDeletionDocs = state.docStatusCards.map((doc) => {
        if (doc.markForDeletion) return doc.documentId;
      });

      documents.forEach((doc) => {
        if (!markForDeletionDocs.includes(doc.documentId)) {
          retainedDocs.push(doc);
          retainedDocIds.push(doc.documentId);
        }
      });

      const currentDocsWithoutIncomingIds = state.docStatusCards.filter(
        (doc) => !retainedDocIds.includes(doc.documentId)
      );

      state.docStatusCards = [...currentDocsWithoutIncomingIds, ...retainedDocs];
    },

    openOldUiFlyoutContentUpload: (state, action: PayloadAction<{ oldUiFlyoutContent: OldUiFlyoutContentType }>) => {
      state.oldUiFlyoutContent = action.payload.oldUiFlyoutContent;
    },
    updateButtonDelegationEnabled: (
      state,
      action: PayloadAction<OldUiFlyoutContentType["buttonDelegationEnabled"]>
    ) => {
      state.oldUiFlyoutContent.buttonDelegationEnabled = action.payload;
    },
  },
});

export const getDocStatusCards = (state: RootState) => state.docUpload.docStatusCards;
export const {
  setActivePage,
  setFile,
  removeFile,
  removeAllFiles,
  toggleSubmitButton,
  removeFromDocStatusCards,
  updateDocStatusCardsFromEDM,
  setRequiredPagesForFileUpload,
  setSelectedFileType,
  setIsCountryWithDownloadableContent,
  setDownloadableDocs,
  openOldUiFlyoutContentUpload,
  updateButtonDelegationEnabled,
} = docUploadSlice.actions;

export const getCurrentPage = (state: RootState) => state.docUpload.currentPage;
export const getIsCountryWithDownloadableContentDocUpload = (state: RootState) =>
  state.docUpload.isCountryWithDownloadableContentDocUpload;
export const getDownloadableDocs = (state: RootState) => state.docUpload.downloadableDocs;
export const getIsActionButtonEnabled = (state: RootState) => state.docUpload.isActionButtonEnabled;
export const getSelectedFileType = (state: RootState) => state.docUpload.selectedFileType;
// export const getRequiredPages = (state: RootState) => state.docUpload.requiredPages;
export const getFiles = (state: RootState) => state.docUpload.files;
export const getOldUiFlyoutContent = (state: RootState) => state.docUpload.oldUiFlyoutContent;

export default docUploadSlice.reducer;
